import {
  STREAM_EVENT,
  type StreamedProgressiveUiComponentData,
} from 'tripPlanner/api/types'
import type { MessagePart } from 'tripPlanner/components/Chat/Message'
import type { StreamEvent } from 'tripPlanner/utils/streamEventParser'
import {
  isStreamedCardOption,
  isStreamedCarouselOfferId,
  isStreamedCarouselOccupancy,
  isStreamedPillOption,
  isStreamedProgressiveUiComponentData,
  isStreamedTestimonialReview,
} from 'tripPlanner/utils/streamedProgressiveUi'

type ProgressiveUiStreamEvent = Extract<
  StreamEvent,
  {
    event:
      | typeof STREAM_EVENT.UI_PART_STARTED
      | typeof STREAM_EVENT.UI_PART_PATCH
      | typeof STREAM_EVENT.UI_PART_COMPLETED
      | typeof STREAM_EVENT.UI_PART_FAILED
  }
>

interface StreamedUiPartsState {
  partIndexes: Map<string, number>
}

interface ApplyProgressiveUiPartEventArgs {
  event: ProgressiveUiStreamEvent
  expectedMessageId: string
  parts: Array<MessagePart>
  state: StreamedUiPartsState
  onPartFailed?: (uiType: StreamedProgressiveUiComponentData['type'], reason: 'server_failed') => void
}

interface ApplyProgressiveUiPartEventResult {
  parts: Array<MessagePart>
  changed: boolean
}

export function createStreamedUiPartsState(): StreamedUiPartsState {
  return { partIndexes: new Map() }
}

export function resetStreamedUiPartsState(state: StreamedUiPartsState): void {
  state.partIndexes = new Map()
}

function rebuildStreamedUiPartIndexes(state: StreamedUiPartsState, parts: Array<MessagePart>): void {
  state.partIndexes = new Map()
  parts.forEach((part, index) => {
    if (part.type === 'ui_component' && isStreamedProgressiveUiComponentData(part.data)) {
      state.partIndexes.set(part.data.streamedPartId, index)
    }
  })
}

function getStreamedUiPartIndex(state: StreamedUiPartsState, parts: Array<MessagePart>, partId: string): number | undefined {
  const index = state.partIndexes.get(partId)
  if (index === undefined) return undefined
  const part = parts[index]
  if (part?.type !== 'ui_component' || !isStreamedProgressiveUiComponentData(part.data)) return undefined
  return index
}

function isSingleSegmentPath(path: Array<string>, segment: string): boolean {
  return path.length === 1 && path[0] === segment
}

export function applyProgressiveUiPartEvent({
  event,
  expectedMessageId,
  parts,
  state,
  onPartFailed,
}: ApplyProgressiveUiPartEventArgs): ApplyProgressiveUiPartEventResult {
  if (event.messageId !== expectedMessageId) return { parts, changed: false }

  if (event.event === STREAM_EVENT.UI_PART_STARTED) {
    if (state.partIndexes.has(event.partId)) return { parts, changed: false }

    const data: StreamedProgressiveUiComponentData = {
      ...event.data,
      streamedPartId: event.partId,
      streamingStatus: 'loading',
    }
    state.partIndexes.set(event.partId, parts.length)
    parts.push({ type: 'ui_component', data })
    return { parts, changed: true }
  }

  const index = getStreamedUiPartIndex(state, parts, event.partId)
  if (index === undefined) return { parts, changed: false }

  const part = parts[index]
  if (part?.type !== 'ui_component' || !isStreamedProgressiveUiComponentData(part.data)) {
    return { parts, changed: false }
  }

  if (event.event === STREAM_EVENT.UI_PART_PATCH) {
    if (
      event.op === 'append' &&
      isSingleSegmentPath(event.path, 'offerIds') &&
      part.data.type === 'carousel' &&
      isStreamedCarouselOfferId(event.value)
    ) {
      parts[index] = {
        type: 'ui_component',
        data: {
          ...part.data,
          streamingStatus: 'streaming',
          offerIds: [...part.data.offerIds, event.value],
        },
      }
      return { parts, changed: true }
    }

    if (
      event.op === 'append' &&
      isSingleSegmentPath(event.path, 'options') &&
      part.data.type === 'pills_list' &&
      isStreamedPillOption(event.value)
    ) {
      parts[index] = {
        type: 'ui_component',
        data: {
          ...part.data,
          streamingStatus: 'streaming',
          options: [...part.data.options, event.value],
        },
      }
      return { parts, changed: true }
    }

    if (
      event.op === 'append' &&
      isSingleSegmentPath(event.path, 'options') &&
      part.data.type === 'cards_list' &&
      isStreamedCardOption(event.value)
    ) {
      parts[index] = {
        type: 'ui_component',
        data: {
          ...part.data,
          streamingStatus: 'streaming',
          options: [...part.data.options, event.value],
        },
      }
      return { parts, changed: true }
    }

    if (
      event.op === 'append' &&
      isSingleSegmentPath(event.path, 'reviews') &&
      part.data.type === 'testimonial' &&
      isStreamedTestimonialReview(event.value)
    ) {
      parts[index] = {
        type: 'ui_component',
        data: {
          ...part.data,
          streamingStatus: 'streaming',
          reviews: [...part.data.reviews, event.value],
        },
      }
      return { parts, changed: true }
    }

    if (event.op === 'set' && isSingleSegmentPath(event.path, 'select') && (event.value === 'single' || event.value === 'multiple')) {
      if (part.data.type === 'testimonial' || part.data.type === 'carousel') return { parts, changed: false }
      parts[index] = {
        type: 'ui_component',
        data: {
          ...part.data,
          streamingStatus: 'streaming',
          select: event.value,
        },
      }
      return { parts, changed: true }
    }

    if (
      part.data.type === 'carousel' &&
      event.op === 'set' &&
      isSingleSegmentPath(event.path, 'title') &&
      typeof event.value === 'string'
    ) {
      parts[index] = {
        type: 'ui_component',
        data: {
          ...part.data,
          streamingStatus: 'streaming',
          title: event.value,
        },
      }
      return { parts, changed: true }
    }

    if (
      part.data.type === 'carousel' &&
      event.op === 'set' &&
      isSingleSegmentPath(event.path, 'occupancy') &&
      isStreamedCarouselOccupancy(event.value)
    ) {
      parts[index] = {
        type: 'ui_component',
        data: {
          ...part.data,
          streamingStatus: 'streaming',
          occupancy: event.value,
        },
      }
      return { parts, changed: true }
    }

    if (
      part.data.type === 'cards_list' &&
      event.op === 'set' &&
      isSingleSegmentPath(event.path, 'categories') &&
      (event.value === 'generic' || event.value === 'places' || event.value === 'locations')
    ) {
      parts[index] = {
        type: 'ui_component',
        data: {
          ...part.data,
          streamingStatus: 'streaming',
          categories: event.value,
        },
      }
      return { parts, changed: true }
    }

    return { parts, changed: false }
  }

  if (event.event === STREAM_EVENT.UI_PART_COMPLETED) {
    if (event.data.type !== part.data.type) {
      console.warn(`[trip-chat] Progressive UI completion type mismatch for partId ${event.partId}: expected ${part.data.type}, got ${event.data.type}`)
      return { parts, changed: false }
    }

    const completedData: StreamedProgressiveUiComponentData = event.data.type === 'carousel' &&
      part.data.type === 'carousel' &&
      event.data.occupancy == null &&
      isStreamedCarouselOccupancy(part.data.occupancy)
      ? { ...event.data, occupancy: part.data.occupancy, streamedPartId: event.partId, streamingStatus: 'complete' }
      : { ...event.data, streamedPartId: event.partId, streamingStatus: 'complete' }

    parts[index] = {
      type: 'ui_component',
      data: completedData,
    }
    return { parts, changed: true }
  }

  onPartFailed?.(part.data.type, 'server_failed')
  parts.splice(index, 1)
  rebuildStreamedUiPartIndexes(state, parts)
  return { parts, changed: true }
}
